import { useCallback, useEffect, useRef, useState } from "react";
import { useRecoilState } from "recoil";
import {
  fileUploaderConnectionIdState,
  fileUploaderSessionActiveState,
  openFileUploaderList,
  pinFileUploaderState,
  uploadingFilesState,
} from "@/store/file-uploader-state";
import { usePolling } from "@/hooks/polling/use-polling";
import { POLLING_INTERVALS } from "@/hooks/polling/polling-intervals";

const CONNECTION_WAIT_MS = 15000;
const CONNECTION_POLL_MS = 50;

const useFilesUploaderSocketIO = () => {
  const [connectionId, setConnectionId] = useState<string>();
  const [data, setData] = useState<any>();
  const [sessionActive, setSessionActive] = useRecoilState(
    fileUploaderSessionActiveState
  );
  const [, setFiles] = useRecoilState(uploadingFilesState);
  const [, setShowFileUploader] = useRecoilState(pinFileUploaderState);
  const [, setOpen] = useRecoilState(openFileUploaderList);
  const [, setConnectionIdState] = useRecoilState(fileUploaderConnectionIdState);

  const connectionIdRef = useRef<string | undefined>();

  const { connectionId: pollingConnectionId } = usePolling({
    enabled: sessionActive,
    pollImmediately: true,
    intervalMs: POLLING_INTERVALS.filesUploader,
    events: {
      UpdateFileUploader: (newData) => {
        const payload = newData as any[];
        if (payload && payload.length > 0) {
          for (let i = 0; i < payload.length; i++) {
            if (payload[i].productType) {
              payload[i].productType = payload[i].productType.replaceAll(
                "\\",
                ""
              );
              payload[i].productType = payload[i].productType.replaceAll(
                '"',
                ""
              );
            }
          }
        }
        setFiles(payload);
        setData(payload);
        setShowFileUploader(payload?.length > 0);
        setOpen(payload?.length > 0);
      },
    },
  });

  useEffect(() => {
    if (pollingConnectionId) {
      connectionIdRef.current = pollingConnectionId;
      setConnectionId(pollingConnectionId);
      setConnectionIdState(pollingConnectionId);
    }
  }, [pollingConnectionId, setConnectionIdState]);

  useEffect(() => {
    if (!sessionActive) {
      connectionIdRef.current = undefined;
      setConnectionId(undefined);
      setConnectionIdState("");
    }
  }, [sessionActive, setConnectionIdState]);

  const waitForConnectionId = useCallback(async (): Promise<string> => {
    const deadline = Date.now() + CONNECTION_WAIT_MS;
    while (Date.now() < deadline) {
      const id = connectionIdRef.current;
      if (id) {
        return id;
      }
      await new Promise((resolve) => setTimeout(resolve, CONNECTION_POLL_MS));
    }
    throw new Error("File uploader session connection timed out");
  }, []);

  const activateFileUploaderSession = useCallback(async (): Promise<string> => {
    if (connectionIdRef.current) {
      setSessionActive(true);
      return connectionIdRef.current;
    }
    setSessionActive(true);
    return waitForConnectionId();
  }, [setSessionActive, waitForConnectionId]);

  const deactivateFileUploaderSession = useCallback(() => {
    setSessionActive(false);
  }, [setSessionActive]);

  return {
    connectionId: pollingConnectionId ?? connectionId,
    data,
    activateFileUploaderSession,
    deactivateFileUploaderSession,
    closeConnection: deactivateFileUploaderSession,
  };
};

export { useFilesUploaderSocketIO };
